/**
 * Painel de chat de teste de fluxo
 * Permite testar a execução do fluxo visualmente com simulação de conversa
 * Executa webhooks reais via Edge Function para testar integrações
 * 
 * RELEASE: 1.1.0 - 2024-12-24
 * - Adicionado suporte a tenantId para execução real de webhooks
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from 'next-themes';
import { FlowNode, FlowEdge } from '@/types/flows';
import { useFlowTestEngine } from '@/hooks/useFlowTestEngine';
import { FlowTestMessage } from './FlowTestMessage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { X, RotateCcw, ChevronDown, Send, Code } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { applyMask, getMaskPlaceholder } from '@/lib/input-masks';
import { useAuth } from '@/hooks/useAuth';

interface FlowTestChatProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodes: FlowNode[];
  edges: FlowEdge[];
  onHighlightNode: (nodeId: string | null) => void;
  /** ID do node a partir do qual iniciar o teste (null = inicia do start) */
  startFromNodeId?: string | null;
}

/**
 * Componente de chat de teste lateral
 */
export function FlowTestChat({ open, onOpenChange, nodes, edges, onHighlightNode, startFromNodeId }: FlowTestChatProps) {
  // Hook para detectar tema (dark/light) - usado para condicionar imagem de fundo
  const { theme } = useTheme();
  
  // Obtém tenantId para chamadas reais de webhook
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;
  
  const {
    messages,
    variables,
    currentNodeId,
    nodes: engineNodes,
    isWaitingInput,
    isRunning,
    highlightedNodeId,
    startTest,
    stopTest,
    restartTest,
    submitResponse,
  } = useFlowTestEngine({ tenantId });

  const [inputValue, setInputValue] = useState('');
  const [showVariables, setShowVariables] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasStartedRef = useRef(false);

  /**
   * Detecta o tipo de máscara baseado no node atual
   */
  const maskType = useMemo(() => {
    if (!currentNodeId || !engineNodes.length) return null;
    
    const currentNode = engineNodes.find(n => n.id === currentNodeId);
    if (!currentNode || currentNode.data.type !== 'input_data') return null;
    
    const config = currentNode.data.config;
    if (!config?.data_type) return null;
    
    // Retorna tipo de máscara se for phone, cpf, cnpj ou date
    if (config.data_type === 'phone' || config.data_type === 'cpf' || config.data_type === 'cnpj' || config.data_type === 'date') {
      return config.data_type;
    }
    
    return null;
  }, [currentNodeId, engineNodes]);

  // Auto-scroll para última mensagem
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Propaga node destacado para o canvas
  useEffect(() => {
    onHighlightNode(highlightedNodeId);
  }, [highlightedNodeId, onHighlightNode]);

  // Inicia teste quando abre o painel
  useEffect(() => {
    if (open && nodes.length > 0 && edges.length > 0 && !hasStartedRef.current) {
      hasStartedRef.current = true;
      // Se startFromNodeId fornecido, inicia a partir desse node
      startTest(nodes, edges, startFromNodeId || undefined);
    }
    
    // Reset quando fecha
    if (!open) {
      hasStartedRef.current = false;
    }
  }, [open, nodes, edges, startTest, startFromNodeId]);

  /**
   * Aplica máscara no input conforme usuário digita
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const maskedValue = applyMask(rawValue, maskType);
    setInputValue(maskedValue);
  };

  /**
   * Envia resposta de input
   */
  const handleSendInput = () => {
    if (!inputValue.trim()) return;
    submitResponse(inputValue.trim());
    setInputValue('');
  };

  /**
   * Reinicia teste
   */
  const handleRestart = () => {
    startTest(nodes, edges);
  };

  /**
   * Fecha painel
   */
  const handleClose = () => {
    stopTest();
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[400px] p-0 flex flex-col">
        {/* Header */}
        <SheetHeader className="px-4 py-3 border-b">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-base">Testar Fluxo</SheetTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleRestart}
                title="Reiniciar teste"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={handleClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </SheetHeader>

        {/* Status */}
        <div className="px-4 py-2 border-b bg-muted/30">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              {isRunning ? (
                <Badge variant="secondary" className="gap-1">
                  <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
                  Executando
                </Badge>
              ) : (
                <Badge variant="outline">Parado</Badge>
              )}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={() => setShowVariables(!showVariables)}
            >
              <Code className="h-3 w-3 mr-1" />
              Variáveis ({Object.keys(variables).length})
            </Button>
          </div>
        </div>

        {/* Painel de variáveis (colapsável) */}
        {showVariables && (
          <div className="px-4 py-2 border-b bg-muted/20">
            <Collapsible open={showVariables} onOpenChange={setShowVariables}>
              <CollapsibleContent>
                <div className="text-xs space-y-1">
                  {Object.keys(variables).length === 0 ? (
                    <p className="text-muted-foreground italic">Nenhuma variável coletada</p>
                  ) : (
                    Object.entries(variables).map(([key, value]) => (
                      <div key={key} className="flex justify-between gap-2 py-1">
                        <span className="font-mono text-primary">{key}:</span>
                        <span className="font-mono text-muted-foreground truncate">
                          {String(value)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        )}

        {/* Área de mensagens com background estilo WhatsApp */}
        <ScrollArea 
          className="flex-1"
          style={{
            backgroundColor: 'hsl(var(--chat-background))',
            /* Plano de fundo: imagem escura no modo noturno, whatsapp no modo claro */
            backgroundImage: theme === 'dark' ? 'url(https://api.uaizychat.com.br/storage/v1/object/public/anexos/system/branding_fundo_preto.png)' : 'url(/system/chat-background.jpg)',
            backgroundRepeat: 'repeat',
            backgroundSize: 'auto',
          }}
        >
          <div className="px-4 py-3 min-h-full">
            {messages.length === 0 ? (
              <div className="flex items-center justify-center h-full text-center text-muted-foreground text-sm">
                <div className="bg-background/80 rounded-lg px-4 py-3">
                  <p>Nenhuma mensagem ainda.</p>
                  <p className="text-xs mt-1">O fluxo iniciará automaticamente.</p>
                </div>
              </div>
            ) : (
              <div>
                {messages.map(message => (
                  <FlowTestMessage
                    key={message.id}
                    message={message}
                    onButtonClick={(value) => submitResponse(value)}
                  />
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Input de resposta (quando aguardando input) */}
        {isWaitingInput && (
          <div className="px-4 py-3 border-t bg-background">
            <div className="flex gap-2">
              <Input
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={(e) => e.key === 'Enter' && handleSendInput()}
                placeholder={getMaskPlaceholder(maskType)}
                className="flex-1"
                autoFocus
              />
              <Button
                size="icon"
                onClick={handleSendInput}
                disabled={!inputValue.trim()}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
            {maskType && (
              <p className="text-xs text-muted-foreground mt-1">
                {maskType === 'phone' && 'Digite apenas os números'}
                {maskType === 'cpf' && 'Digite apenas os números'}
                {maskType === 'cnpj' && 'Digite apenas os números'}
                {maskType === 'date' && 'Formato: DD/MM/AAAA'}
              </p>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
